/**
 * StreamItemsTracker is a utility class that serves a number of functions necessary
 * for analytics and updating the URL bar as readers scroll through a stream.
 *
 * The tracker maintains an Immutable Map of stream items. The keys of the map are DOM nodes and the values
 * are a nested Immutable map with values for isInView, status, position, and the GraphQL edge data for
 * the stream item.
 *
 * StreamItemTracker uses a concept we call candidacy. When whatever implements StreamItemTracker declares
 * a DOM Node a candidate, a timeout function starts. If the DOM node maintains its candidacy for the duration
 * of the timeout, an onItemViewed function is called. The implementer declares and removes candidacies.
 *
 * @param {number} timeoutLength - length of candidacy before calling onItemViewed.
 * @param {function} onItemViewed - called when candidate timeout ends, passed map entry as an object.
 * @param {function} onStatusChange - called when any entry's status changes
 */

import { Map } from "immutable";

export const STATUS = {
  NULL: null,
  CANDIDATE: "CANDIDATE",
  COUNTED: "COUNTED", // COUNTED is only used to set background-color when ANALYTICS_DEBUG=true
};

export default class StreamItemsTracker {
  constructor(
    timeoutLength,
    onVirtualPageView,
    onItemCandidacy,
    onStatusChange
  ) {
    this.trackedItemsMap = Map();
    this.timeoutLength = timeoutLength;
    this.onVirtualPageView = onVirtualPageView;
    this.onItemCandidacy = onItemCandidacy;
    this.onStatusChange = onStatusChange;
    this.handleIntersect = this._handleIntersect.bind(this);
    this.observer = this.createIntersectionObserver();
  }

  setTrackedDomNodeStatus(domNode, status, skipHandler) {
    const currentStatus = this.trackedItemsMap.getIn([domNode, "status"]);
    const hasStatusTruthynessChanged = !!currentStatus ^ !!status; // false if toggling between CANDIDATE and COUNTED
    this.trackedItemsMap = this.trackedItemsMap.setIn(
      [domNode, "status"],
      status
    );

    // Open the browser console and set window.ANALYTICS_DEBUG to true to enable visual status changes
    if (window.ANALYTICS_DEBUG) {
      this.setDomNodeColorByStatus(domNode, status);
    }

    // skipHandler is used by trackFirstItem
    if (!skipHandler && hasStatusTruthynessChanged) {
      this.onStatusChange();
    }
  }

  setDomNodeColorByStatus(domNode, status) {
    const cardWrapper = domNode.firstChild;
    switch (status) {
      case STATUS.NULL:
        cardWrapper.style.backgroundColor = "#ffffff";
        break;
      case STATUS.CANDIDATE:
        cardWrapper.style.backgroundColor = "#ff7921";
        break;
      case STATUS.COUNTED:
        cardWrapper.style.backgroundColor = "#009a68";
        break;
    }
  }

  /**
   * Add a new item to the Map of tracked items and observe the node's intersections
   */
  track(domNode, edge, position) {
    this.trackedItemsMap = this.trackedItemsMap.set(
      domNode,
      Map({ edge, position })
    );
    this.observer.observe(domNode);
  }

  /**
   * Remove item from the map of tracked items and ubobserve the node's intersections
   */
  untrack(domNode) {
    this.trackedItemsMap = this.trackedItemsMap.delete(domNode);
    this.observer.unobserve(domNode);
  }

  /**
   * Returns a new IntersectionObserver with threshold = 0. The callback will run when 1 pixel
   * of an observed entry enters the viewportand when the last pixel of an observed entry leaves the viewport
   */
  createIntersectionObserver() {
    return new IntersectionObserver(this.handleIntersect, { threshold: 0 });
  }

  /**
   * Called when observed entries intersections changes. entries is a list of intersection changes
   * for observed target elements.
   */
  _handleIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.onDomNodeInViewport(entry.target);
      } else {
        this.onDomNodeOutOfViewport(entry.target);
      }
    });
  }

  /**
   * Cleans up state of the IntersectionObserver and removes all candidacies, clearing timeouts
   */
  disconnect() {
    this.observer.disconnect();
    this.getCandidates().forEach((item, domNode) => {
      this.handleRemoveDomNodeCandidacy(domNode);
    });
  }

  /**
   * Returns new Map of entries where isInView is true
   */
  getInViewTrackedItems() {
    return this.trackedItemsMap.filter(item => item.get("isInView"));
  }

  /**
   * Returns new Map of entries where isInView is true and where status is not NULL
   */
  getCandidates() {
    return this.trackedItemsMap.filter(
      item => item.get("status") && item.get("isInView")
    );
  }

  /**
   * When a DOM Node enters the viewport, set isInView to true
   */
  onDomNodeInViewport(domNode) {
    this.trackedItemsMap = this.trackedItemsMap.setIn(
      [domNode, "isInView"],
      true
    );
  }

  /**
   * When a DOM Node leaves the viewport, set isInView to false
   */
  onDomNodeOutOfViewport(domNode) {
    this.trackedItemsMap = this.trackedItemsMap.setIn(
      [domNode, "isInView"],
      false
    );
  }

  /**
   * Sets a DOM node as a candidate. Starts timeout countdown to the onVirtualPageView function
   */
  handleDomNodeCandidacy(domNode) {
    if (this.trackedItemsMap.getIn([domNode, "status"])) {
      return;
    } // Do nothing if entry is already CANDIDATE or COUNTED

    this.setTrackedDomNodeStatus(domNode, STATUS.CANDIDATE);
    this.onItemCandidacy(this.trackedItemsMap.get(domNode).toObject());

    this.trackedItemsMap = this.trackedItemsMap.setIn(
      [domNode, "timeout"],
      setTimeout(() => {
        this.onVirtualPageView(this.trackedItemsMap.get(domNode).toObject());
        this.setTrackedDomNodeStatus(domNode, STATUS.COUNTED);
      }, this.timeoutLength)
    );
  }

  /**
   * Removes DOM candidacy status and clears timeout.
   */
  handleRemoveDomNodeCandidacy(domNode) {
    if (!this.trackedItemsMap.getIn([domNode, "status"])) {
      return;
    } // Do nothing if entry is already NULL

    this.setTrackedDomNodeStatus(domNode, STATUS.NULL);
    clearTimeout(this.trackedItemsMap.getIn([domNode, "timeout"]));
  }

  /**
   * The IntersectionObserver callback isn't called until a user starts scrolling. This lets
   * AnalyticsWrapper put the tracker in the correct state when it is mounted. Some streams could have
   * no stories, so check if a first story exists, before handling it.
   */
  trackFirstItem() {
    const firstTrackedItem = this.trackedItemsMap.findEntry(
      item => item.get("position") === 0
    );
    if (firstTrackedItem) {
      const [domNode, value] = firstTrackedItem;
      this.onDomNodeInViewport(domNode);
      this.onVirtualPageView(value.toObject());
      this.setTrackedDomNodeStatus(domNode, STATUS.COUNTED, true);
    }
  }
}
